import {
  Component,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  Market,
  PaymentMethod,
  PaymentRequestDto,
  PaymentRequestStatus,
} from '@app/model/payment-request/payment-request.dto';
import { LoggingService } from '@app/service/logging-service/logging.service';
import { QueryParamsService } from '@app/service/query-params/query-params.service';
import {
  AtRoute,
  DeRoute,
  NoRoute,
  FiRoute,
  routeNames,
} from '@assets/val/route-constants';
import { Store } from '@ngxs/store';
import { StateService } from '@app/service/state/state.service';
import { IframeService, WindowMode } from '@app/service/iframe/iframe.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { CreditDecisionDialogComponent } from '@app/component/dialog/credit-decision/credit-decision-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import Translations from './iframe-landing.component.json';
import { Subscription } from 'rxjs';
import { LoaderMessagingService } from '@app/service/loader-messaging/loader-messaging.service';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'iframe-landing',
  templateUrl: './iframe-landing.component.html',
  styleUrls: ['../page-shared.css'],
})
export class IframeLandingComponent implements OnInit, OnDestroy {
  constructor(
    private store: Store,
    private ngZone: NgZone,
    private router: Router,
    private stateService: StateService,
    private dialogService: MatDialog,
    private iframeService: IframeService,
    private loggingService: LoggingService,
    private queryParamsService: QueryParamsService,
    private deviceDetectorService: DeviceDetectorService,
    private loadermessagingService: LoaderMessagingService
  ) {}

  token: string;
  market: Market;
  isError: boolean = false;
  isIframe: boolean;
  isMobile: boolean = false;
  homeRoute: string;
  childWindow: Window;
  translations: { [key: string]: string };
  isLandingError: boolean = this.stateService.getLandingError();
  paymentRequest: PaymentRequestDto;
  tabClosedSubscription: Subscription;

  // Error messages
  errorHeading: string = 'Oops! Something went wrong.';
  errorInfo: string = 'Please try to reload the page or try again later.';

  async ngOnInit(): Promise<void> {
    this.loggingService.warn(
      `Iframe landing was loaded from route: ${window.location.href}`
    );

    // If our iframe loader is inside another iframe ('inIframe' param is set by loader) ---> Display error...
    if (this.queryParamsService.getQueryParams().inIframe === 'true') {
      this.errorHeading = 'You cannot load our script inside of a Iframe.';
      this.errorInfo =
        'To ensure proper functionality, the script must be placed outside the iframe and at the top level of the window. Otherwise, you may encounter COOP (Cross-Origin Opener Policy) issues during the workflow.';
      this.isError = true; //Displays error message

      this.loggingService.reportCrash(
        'User attempted to load iframe flow inside another iframe (iframe-ception), displaying error instead.'
      );

      return;
    }

    await this.getPaymentRequestById().then((response: PaymentRequestDto) => {
      if (!response) {
        this.isError = true; //Displays error message

        this.loggingService.reportCrash(
          `No payment request found for token ${this.token}.`
        );
        return;
      }

      this.paymentRequest = response;
      this.setup();
    });
  }

  async ngOnDestroy(): Promise<void> {
    if (this.tabClosedSubscription) {
      this.tabClosedSubscription.unsubscribe();
    }
  }

  @HostListener('window:unload', ['$event'])
  unloadHandler(event: Event) {
    //('Clean up tab if iframe is closed or reloaded');
    if (this.isIframe) {
      this.loadermessagingService.closeChildWindow(this.childWindow);
    }
  }

  setup(): void {
    this.market = this.paymentRequest.market;
    this.isIframe = this.iframeService.isThisInIframe();
    this.isMobile = this.deviceDetectorService.isMobile();
    this.translations =
      Translations[
        this.stateService.getCurrentStateLocaleBaseCode(
          this.paymentRequest.language
        )
      ];

    this.loggingService.warn(
      `Iframe landing found payment request with id: '${this.token}' and got the market: '${this.market}'`
    );

    if (
      this.paymentRequest.specificPaymentMethod &&
      this.paymentRequest.specificPaymentMethodType &&
      this.paymentRequest.status !== PaymentRequestStatus.SETTLED
    ) {
      this.openSpecificPaymentMethodByWindowMode();
    } else {
      // Home route for payment request (/at, /de, /fi, /, /no)
      switch (this.market) {
        case Market.AT:
          this.homeRoute = AtRoute.HOME;
          break;
        case Market.DE:
          this.homeRoute = DeRoute.HOME;
          break;
        case Market.FI:
          this.homeRoute = FiRoute.HOME;
          break;
        case Market.SE: // SE will only work for FE driven.
          this.homeRoute = routeNames.HOME;
          break;
        case Market.NO:
          this.homeRoute = NoRoute.HOME;
          break;
        default:
          this.homeRoute = routeNames.ERROR;
          break;
      }

      this.router.navigate([`/${this.homeRoute}`], {
        queryParamsHandling: 'preserve',
      });
    }
  }

  async getPaymentRequestById(): Promise<PaymentRequestDto> {
    this.token = this.queryParamsService.getQueryParams().id;

    if (!this.token && this.store.snapshot().paymentRequest.request.id) {
      this.loggingService.reportCrash(
        'failed to get id from url in queryParamsService. Resetting from PR',
        'url=' + window.location.href,
        null
      );
      this.queryParamsService.setQueryParam(
        'id',
        this.store.snapshot().paymentRequest.request.id
      );

      this.token = this.queryParamsService.getQueryParams().id;
    }

    if (!this.token && !this.store.snapshot().paymentRequest.request.id) {
      this.loggingService.reportCrash(
        'Failed to fetch payment request id for payment request',
        'url=' + window.location.href,
        null
      );
      return;
    }

    return this.stateService.fetchPaymentRequest(this.token);
  }

  async openSpecificPaymentMethodByWindowMode(): Promise<void> {
    const specificRoute: string =
      await this.getSpecificPaymentMethodRouteForMarket();

    const windowMode: string =
      this.queryParamsService.getQueryParams().windowMode;

    if (this.isIframe && windowMode === WindowMode.POPUP) {
      this.createTabClosedSubscription();
      this.childWindow = this.iframeService.openZaverPopup(
        `/${specificRoute}?id=${this.token}`
      );

      // Open up blocker/modal saying another window is open...
      this.createNewWindowBlocker(true);
    } else if (
      (this.isIframe && windowMode === WindowMode.TAB) ||
      (this.isIframe &&
        this.isMobile &&
        !this.iframeService.isThisAChildWindow())
    ) {
      this.createTabClosedSubscription();
      this.childWindow = this.iframeService.openZaverTab(
        `/${specificRoute}?id=${this.token}`
      );

      // Open up blocker/modal saying another window is open...
      this.createNewWindowBlocker(true);
    } else {
      // Navigate as usual to the specific payment method's 'home' page.
      this.router.navigate([`/${specificRoute}`], {
        queryParamsHandling: 'preserve',
        queryParams: {
          method: this.paymentRequest.specificPaymentMethod,
          methodType: this.paymentRequest.specificPaymentMethodType,
        },
      });
    }
  }

  createButtons() {
    let buttons = [];
    buttons.push({
      title: this.translations.open,
      callback: () => {
        // Focus on childWindow when it exists (desktop only). Else we open a new window.
        if (this.childWindow && !this.isMobile) {
          this.childWindow.focus();
          this.createNewWindowBlocker(false);
        } else {
          this.openSpecificPaymentMethodByWindowMode();
        }
      },
    });

    return buttons;
  }

  createNewWindowBlocker(useInitialPopupTranslation: boolean): void {
    const dialogConfiguration = {
      maxHeight: '75vh',
      maxWidth: '90vw',
      data: {
        title: useInitialPopupTranslation
          ? this.translations.initTitle
          : this.translations.title,
        reason: useInitialPopupTranslation
          ? this.translations.initReason
          : this.translations.reason,
        buttons: this.createButtons(),
      },
    };
    this.dialogService.open(CreditDecisionDialogComponent, dialogConfiguration);
  }

  createTabClosedSubscription(): void {
    if (!this.tabClosedSubscription) {
      this.tabClosedSubscription = this.loadermessagingService.event$
        .pipe(filter((message) => message === 'closed')) // Loader Messaging Service sent message 'closed'
        .subscribe(() => {
          this.ngZone.run(() => {
            this.childWindow = null;
          });
        });
    }
  }

  async getSpecificPaymentMethodRouteForMarket(): Promise<string> {
    if (this.market === Market.SE) {
      // TODO REMOVE WHEN MOVING SE TO BACKEND DRIVEN CHECKOUT
      const useBackendDrivenFlow = this.paymentRequest?.specificPaymentMethodType === PaymentMethod.INSTANT_DEBIT;
      if (useBackendDrivenFlow) {
        return this.stateService
          .registerChosenMethod(this.paymentRequest?.specificPaymentMethod)
          .then((checkoutState) => {
            return this.stateService.getRouteFromState(checkoutState);
          });
      } else {
        this.getFrontendDrivenRoute();
      }
    } else {
      return this.stateService
        .registerChosenMethod(this.paymentRequest.specificPaymentMethod)
        .then((checkoutState) => {
          return this.stateService.getRouteFromState(checkoutState);
        });
    }
  }

  getFrontendDrivenRoute() {
    switch (this.paymentRequest?.specificPaymentMethodType) {
      case PaymentMethod.TRUSTLY:
        return routeNames.PERSONAL_NUMBER_ENTRY;
      case PaymentMethod.INVOICE:
      case PaymentMethod.CREDIT:
      case PaymentMethod.CREDIT_STATIC_AMOUNT:
        return routeNames.AUTH;
      case PaymentMethod.SWISH_EXTERNAL:
        return routeNames.SWISH;
      case PaymentMethod.INSTANT_DEBIT:
        return routeNames.TRUSTLY_DIRECT_DEBIT;
    }
  }
}
